// @ts-nocheck
/**
 * One line (or one block) of the director's own words, edited where it is read.
 *
 * Matches the renames that already exist on the bracket: click the pencil, type,
 * Enter saves, Esc cancels. Nothing is a dialog. An empty note shows a faint
 * "+ Add a note" while the row is hovered and nothing at all otherwise, so a
 * bracket with no notes carries no furniture.
 *
 * The text is plain text. React escapes it on the way out, and the way in is a
 * plain input, so <b>test</b> reaches the public page as those characters.
 */
import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Pencil } from 'lucide-react';

export function noteLimitSentence(value: string, limit: number): string | null {
  const n = value.length;
  if (n > limit) {
    const over = n - limit;
    return `That is ${n} characters. The limit is ${limit} — take ${over} off.`;
  }
  if (n >= limit - 20) return `${limit - n} character${limit - n === 1 ? '' : 's'} left of ${limit}.`;
  return null;
}

type Props = {
  value?: string | null;
  canEdit?: boolean;
  limit: number;
  multiline?: boolean;
  addLabel?: string;
  className?: string;
  editTitle?: string;
  onSave: (next: string) => Promise<boolean | void> | boolean | void;
};

export const InlineNote: React.FC<Props> = ({
  value,
  canEdit,
  limit,
  multiline,
  addLabel = '+ Add a note',
  className = '',
  editTitle = 'Add a note',
  onSave,
}) => {
  const text = String(value ?? '').trim();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const warn = noteLimitSentence(draft, limit);
  const tooLong = draft.length > limit;

  const commit = async () => {
    if (tooLong) return;
    setBusy(true);
    try {
      await onSave(draft.trim());
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  if (!canEdit) {
    if (!text) return null;
    return (
      <div className={`whitespace-pre-wrap text-[11px] text-muted-foreground ${className}`} data-note>
        {text}
      </div>
    );
  }

  if (editing) {
    const common = {
      autoFocus: true,
      value: draft,
      disabled: busy,
      onClick: (e: React.MouseEvent) => e.stopPropagation(),
      onChange: (e: any) => setDraft(e.target.value),
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void commit(); }
        if (e.key === 'Escape') { e.preventDefault(); setEditing(false); }
      },
    };
    return (
      <div className={className} data-note-editor>
        {multiline
          ? <Textarea {...common} rows={3} className="text-xs" />
          : <Input {...common} className="h-6 text-[11px]" />}
        {warn && (
          <p className={`mt-0.5 text-[10px] ${tooLong ? 'text-destructive' : 'text-muted-foreground'}`}>
            {warn}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={`flex items-start gap-1 ${className}`} data-note>
      {text ? (
        <span className="whitespace-pre-wrap text-[11px] text-muted-foreground">{text}</span>
      ) : (
        <button
          type="button"
          className="text-[11px] italic text-muted-foreground opacity-0 transition-opacity hover:underline focus:opacity-100 group-hover:opacity-60"
          onClick={(e) => { e.stopPropagation(); setDraft(''); setEditing(true); }}
        >
          {addLabel}
        </button>
      )}
      {text && (
        <Button
          size="icon"
          variant="ghost"
          className="h-4 w-4 shrink-0 opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100"
          title={editTitle}
          onClick={(e) => { e.stopPropagation(); setDraft(text); setEditing(true); }}
        >
          <Pencil className="h-2.5 w-2.5" />
        </Button>
      )}
    </div>
  );
};

export default InlineNote;
